/**
 * The two data-carrying transitions of a Sozialdepartement item (#2003):
 * "Mark filed" (the filing date) and "Record the decision" (approved with the
 * decision date and number, or rejected with its date). Both write the
 * contract version on the server in the same transaction as the transition.
 *
 * No <form> element on purpose: the dialogs open from inside the workflow
 * detail sheet; a submit event would bubble through the portal.
 */
import { useEffect, useState } from 'react';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/core/macro';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { formatLocalDate } from '@/utils/dateUtils';
import { useFileSubsidyReport, useRecordSubsidyReportDecision } from '@/hooks/useSubsidyReports';
import { subsidy_RecordSubsidyReportDecisionRequest } from '@/api/models/subsidy_RecordSubsidyReportDecisionRequest';
import { backendErrorText } from '@/lib/translateBackendError';

/** The decision number is VARCHAR(16) on the contract version. */
export const DECISION_NUMBER_MAX_LENGTH = 16;

function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function isFuture(date: Date | undefined): boolean {
  return !!date && date.getTime() > startOfToday().getTime();
}

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemId: string;
}

export function SubsidyReportFileDialog({ open, onOpenChange, itemId }: DialogProps) {
  const { i18n } = useLingui();
  const [filedOn, setFiledOn] = useState<Date | undefined>(startOfToday());
  const mutation = useFileSubsidyReport(itemId);

  useEffect(() => {
    if (open) setFiledOn(startOfToday());
  }, [open]);

  const future = isFuture(filedOn);
  const busy = mutation.isPending;

  const save = () => {
    if (!filedOn || future) return;
    mutation.mutate({ filed_on: formatLocalDate(filedOn) }, { onSuccess: () => onOpenChange(false) });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent className="sm:max-w-md" data-testid="subsidy-report-file-dialog">
        <DialogHeader>
          <DialogTitle>{i18n._(msg({ id: 'subsidyReport.file.title', message: 'Mark filed' }))}</DialogTitle>
          <DialogDescription>
            {i18n._(
              msg({
                id: 'subsidyReport.file.description',
                message: 'The day you submitted it in Kollab. It is also saved on the contract.',
              }),
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="subsidy-report-filed-on">
            {i18n._(msg({ id: 'subsidyReport.file.date', message: 'Filed on' }))}
          </Label>
          <DatePicker
            id="subsidy-report-filed-on"
            value={filedOn}
            onChange={setFiledOn}
            aria-invalid={future}
            data-testid="subsidy-report-filed-on"
          />
          {future ? (
            <p className="text-sm text-destructive" role="alert">
              {i18n._(msg({ id: 'subsidyReport.date.future', message: 'The date must not be in the future.' }))}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>
            {i18n._(msg({ id: 'common.cancel', message: 'Cancel' }))}
          </Button>
          <Button type="button" disabled={!filedOn || future || busy} onClick={save} data-testid="subsidy-report-file-save">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            {i18n._(msg({ id: 'subsidyReport.action.markFiled', message: 'Mark filed' }))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type Outcome = 'approved' | 'rejected';

export function SubsidyReportDecisionDialog({ open, onOpenChange, itemId }: DialogProps) {
  const { i18n } = useLingui();
  const [outcome, setOutcome] = useState<Outcome>('approved');
  const [decisionOn, setDecisionOn] = useState<Date | undefined>(startOfToday());
  const [number, setNumber] = useState('');
  const mutation = useRecordSubsidyReportDecision(itemId);

  const resetMutation = mutation.reset;
  useEffect(() => {
    if (!open) return;
    setOutcome('approved');
    setDecisionOn(startOfToday());
    setNumber('');
    resetMutation();
  }, [open, resetMutation]);

  const future = isFuture(decisionOn);
  const numberMissing = outcome === 'approved' && number.trim() === '';
  const busy = mutation.isPending;

  const save = () => {
    if (!decisionOn || future || numberMissing) return;
    mutation.mutate(
      {
        outcome:
          outcome === 'approved'
            ? subsidy_RecordSubsidyReportDecisionRequest.outcome.APPROVED
            : subsidy_RecordSubsidyReportDecisionRequest.outcome.REJECTED,
        decision_on: formatLocalDate(decisionOn),
        decision_number: outcome === 'approved' ? number.trim() : undefined,
      },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent className="sm:max-w-md" data-testid="subsidy-report-decision-dialog">
        <DialogHeader>
          <DialogTitle>
            {i18n._(msg({ id: 'subsidyReport.decision.title', message: 'Record the decision of the Sozialdepartement' }))}
          </DialogTitle>
          <DialogDescription>
            {outcome === 'approved'
              ? i18n._(
                  msg({
                    id: 'subsidyReport.decision.approvedHelp',
                    message: 'Saves the decision on the contract and releases the held invoices.',
                  }),
                )
              : i18n._(
                  msg({
                    id: 'subsidyReport.decision.rejectedHelp',
                    message: 'The invoices stay held until you change the contract or file again.',
                  }),
                )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <RadioGroup
            value={outcome}
            onValueChange={(v) => setOutcome(v === 'rejected' ? 'rejected' : 'approved')}
            className="flex gap-6"
            aria-label={i18n._(msg({ id: 'subsidyReport.decision.outcome', message: 'Decision' }))}
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="approved" id="subsidy-report-outcome-approved" data-testid="subsidy-report-outcome-approved" />
              <Label htmlFor="subsidy-report-outcome-approved">
                {i18n._(msg({ id: 'subsidyReport.decision.approved', message: 'Approved' }))}
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="rejected" id="subsidy-report-outcome-rejected" data-testid="subsidy-report-outcome-rejected" />
              <Label htmlFor="subsidy-report-outcome-rejected">
                {i18n._(msg({ id: 'subsidyReport.decision.rejected', message: 'Rejected' }))}
              </Label>
            </div>
          </RadioGroup>
          <div className="space-y-2">
            <Label htmlFor="subsidy-report-decision-on">
              {i18n._(msg({ id: 'subsidyReport.decision.date', message: 'Decision date' }))}
            </Label>
            <DatePicker
              id="subsidy-report-decision-on"
              value={decisionOn}
              onChange={setDecisionOn}
              aria-invalid={future}
              data-testid="subsidy-report-decision-on"
            />
            {future ? (
              <p className="text-sm text-destructive" role="alert">
                {i18n._(msg({ id: 'subsidyReport.date.future', message: 'The date must not be in the future.' }))}
              </p>
            ) : null}
          </div>
          {mutation.isError ? (
            <p className="text-sm text-destructive" role="alert" data-testid="subsidy-report-decision-error">
              {backendErrorText(
                mutation.error,
                i18n,
                msg({ id: 'subsidyReport.decision.error', message: 'Could not record the decision. Please try again.' }),
              )}
            </p>
          ) : null}
          {outcome === 'approved' ? (
            <div className="space-y-2">
              <Label htmlFor="subsidy-report-decision-number">
                {i18n._(msg({ id: 'subsidyReport.decision.number', message: 'Decision number' }))}
              </Label>
              <Input
                id="subsidy-report-decision-number"
                value={number}
                maxLength={DECISION_NUMBER_MAX_LENGTH}
                onChange={(e) => setNumber(e.target.value)}
                aria-required
                data-testid="subsidy-report-decision-number"
              />
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>
            {i18n._(msg({ id: 'common.cancel', message: 'Cancel' }))}
          </Button>
          <Button
            type="button"
            disabled={!decisionOn || future || numberMissing || busy}
            onClick={save}
            data-testid="subsidy-report-decision-save"
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            {i18n._(msg({ id: 'subsidyReport.decision.save', message: 'Save decision' }))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
